import "../global.css";

import { router, usePathname } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Provider, useDispatch } from "react-redux";

import Menubar from "./menubar";
import { login } from "./redux/actions/authactions";
import store from "./redux/store";
import { LanguageProvider } from "./screens/LanguageProvider";
import Stack from "./stack";

const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
const SESSION_CHECK_INTERVAL = 60 * 1000;
const ACTIVITY_UPDATE_THROTTLE = 30 * 1000;

const LOGIN_PATHS = ["/", "/login"];

const MENUBAR_PATHS = [
  "/screens/home",
  "/screens/menuprofile",
  "/screens/orderprint",
];

/**
 * อ่าน empcode จาก Local Storage
 *
 * รองรับทั้ง:
 * - empcode แบบใหม่
 * - userToken แบบเก่า
 */
function getStoredEmpCode(): string | null {
  if (Platform.OS !== "web") {
    return null;
  }

  try {
    const savedEmpCode = localStorage.getItem("empcode");

    if (savedEmpCode?.trim()) {
      return savedEmpCode.trim();
    }

    const oldUserToken = localStorage.getItem("userToken");

    if (!oldUserToken) {
      return null;
    }

    const parsedToken = JSON.parse(oldUserToken);
    const oldEmpCode = parsedToken?.empcode;

    if (
      typeof oldEmpCode === "string" &&
      oldEmpCode.trim()
    ) {
      const normalizedEmpCode = oldEmpCode.trim();

      localStorage.setItem(
        "empcode",
        normalizedEmpCode,
      );

      return normalizedEmpCode;
    }
  } catch (error) {
    console.error(
      "ไม่สามารถอ่าน userToken เวอร์ชันเก่าได้:",
      error,
    );
  }

  return null;
}

/**
 * ล้าง Session ออกจาก Local Storage
 */
function clearStoredSession(): void {
  if (Platform.OS !== "web") {
    return;
  }

  try {
    localStorage.removeItem("empcode");
    localStorage.removeItem("sessionExpiry");
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
  } catch (error) {
    console.error(
      "ไม่สามารถล้างข้อมูล Session ได้:",
      error,
    );
  }
}

function MainLayout() {
  const pathname = usePathname();
  const dispatch = useDispatch();

  /**
   * Web ต้องกู้คืน Session ก่อนแสดงหน้าจอ
   */
  const [isRestoring, setIsRestoring] = useState(
    Platform.OS === "web",
  );

  /**
   * Android/iOS Production
   * เริ่มด้วยหน้า Loading เพื่อไม่ให้หน้าหลักกระพริบ
   */
  const [isCheckingUpdate, setIsCheckingUpdate] =
    useState(
      Platform.OS !== "web" && !__DEV__,
    );

  const pathnameRef = useRef(pathname);
  const lastActivityUpdateRef = useRef(0);
  const hasRestoredRef = useRef(false);
  const hasCheckedUpdateRef = useRef(false);

  const showMenubar =
    MENUBAR_PATHS.includes(pathname);

  /**
   * อัปเดต pathname ล่าสุดไว้ใน Ref
   */
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  /**
   * ตรวจสอบ EAS Update
   *
   * สำคัญ:
   * ไม่ Import expo-updates ที่ด้านบนของไฟล์
   * เพราะจะทำให้ Web Static Export โหลด Module ไปด้วย
   */
  useEffect(() => {
    if (hasCheckedUpdateRef.current) {
      return;
    }

    hasCheckedUpdateRef.current = true;

    let isMounted = true;

    const updateApp = async () => {
      /**
       * Web และ Development ไม่ตรวจ OTA Update
       */
      if (Platform.OS === "web" || __DEV__) {
        if (isMounted) {
          setIsCheckingUpdate(false);
        }

        return;
      }

      try {
        if (isMounted) {
          setIsCheckingUpdate(true);
        }

        /**
         * Dynamic Import
         * โหลด expo-updates เฉพาะ Android/iOS ตอน Runtime
         */
        const Updates = await import("expo-updates");

        /**
         * ถ้า Build นี้ไม่ได้เปิดใช้ expo-updates
         * ให้เข้าแอปต่อได้ทันที
         */
        if (!Updates.isEnabled) {
          console.log(
            "expo-updates ไม่ได้เปิดใช้งานใน Build นี้",
          );

          return;
        }

        console.log(
          "กำลังตรวจสอบ Application Update...",
        );

        const update =
          await Updates.checkForUpdateAsync();

        if (!update.isAvailable) {
          console.log(
            "Application เป็นเวอร์ชันล่าสุดแล้ว",
          );

          return;
        }

        console.log(
          "พบ Application Update ใหม่ กำลังดาวน์โหลด...",
        );

        await Updates.fetchUpdateAsync();

        console.log(
          "ดาวน์โหลด Update สำเร็จ กำลังเปิดแอปใหม่...",
        );

        await Updates.reloadAsync();
      } catch (error) {
        /**
         * ตรวจสอบ Update ไม่สำเร็จ
         * แอปยังสามารถเปิดใช้งานต่อได้
         */
        console.error(
          "ตรวจสอบ Application Update ไม่สำเร็จ:",
          error,
        );
      } finally {
        if (isMounted) {
          setIsCheckingUpdate(false);
        }
      }
    };

    void updateApp();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * บังคับ Logout บน Web
   */
  const forceLogout = useCallback(() => {
    if (Platform.OS !== "web") {
      return;
    }

    clearStoredSession();

    lastActivityUpdateRef.current = 0;
    setIsRestoring(false);

    if (
      !LOGIN_PATHS.includes(pathnameRef.current)
    ) {
      router.replace("/");
    }
  }, []);

  /**
   * ต่ออายุ Session อีก 60 นาที
   */
  const resetSessionTimer = useCallback(() => {
    if (Platform.OS !== "web") {
      return;
    }

    const savedEmpCode = getStoredEmpCode();

    if (!savedEmpCode) {
      return;
    }

    try {
      const now = Date.now();

      localStorage.setItem(
        "sessionExpiry",
        String(now + INACTIVITY_TIMEOUT),
      );

      lastActivityUpdateRef.current = now;
    } catch (error) {
      console.error(
        "ไม่สามารถต่ออายุ Session ได้:",
        error,
      );
    }
  }, []);

  /**
   * กู้คืน Session เมื่อเปิดหรือ Refresh Web
   */
  useEffect(() => {
    if (Platform.OS !== "web") {
      setIsRestoring(false);
      return;
    }

    if (hasRestoredRef.current) {
      return;
    }

    hasRestoredRef.current = true;

    let isMounted = true;

    const restoreSession = async () => {
      try {
        const savedEmpCode =
          getStoredEmpCode();

        const storedExpiry =
          localStorage.getItem("sessionExpiry");

        const parsedExpiry = storedExpiry
          ? Number(storedExpiry)
          : Number.NaN;

        /**
         * Session เวอร์ชันเก่าอาจมี empcode
         * แต่ยังไม่มี sessionExpiry
         */
        const hasLegacySession = Boolean(
          savedEmpCode && !storedExpiry,
        );

        const hasValidExpiry =
          Number.isFinite(parsedExpiry) &&
          Date.now() < parsedExpiry;

        if (
          !savedEmpCode ||
          (!hasValidExpiry &&
            !hasLegacySession)
        ) {
          forceLogout();
          return;
        }

        if (hasLegacySession) {
          resetSessionTimer();
        }

        /**
         * Login ใหม่เพื่อกู้ Redux State
         */
        const response = await dispatch(
          login(savedEmpCode) as any,
        );

        if (response?.success !== true) {
          console.error(
            "กู้คืน Session ไม่สำเร็จ:",
            response?.message ??
              "Unknown error",
          );

          forceLogout();
          return;
        }

        resetSessionTimer();

        /**
         * มี Session อยู่แล้วแต่เปิดหน้า Login
         * ให้ไปหน้า Home
         */
        if (
          LOGIN_PATHS.includes(
            pathnameRef.current,
          )
        ) {
          router.replace("/screens/home");
        }
      } catch (error) {
        console.error(
          "เกิดข้อผิดพลาดระหว่างกู้คืน Session:",
          error,
        );

        forceLogout();
      } finally {
        if (isMounted) {
          setIsRestoring(false);
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, [
    dispatch,
    forceLogout,
    resetSessionTimer,
  ]);

  /**
   * ตรวจสอบ Activity และ Session Expiry
   */
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    /**
     * ต่ออายุ Session เมื่อผู้ใช้ใช้งาน
     */
    const handleActivity = () => {
      const savedEmpCode =
        getStoredEmpCode();

      if (!savedEmpCode) {
        return;
      }

      const now = Date.now();

      const storedExpiry =
        localStorage.getItem("sessionExpiry");

      const parsedExpiry = storedExpiry
        ? Number(storedExpiry)
        : Number.NaN;

      if (!storedExpiry) {
        resetSessionTimer();
        return;
      }

      if (
        !Number.isFinite(parsedExpiry) ||
        now >= parsedExpiry
      ) {
        forceLogout();
        return;
      }

      /**
       * ป้องกันการเขียน Local Storage
       * ทุกครั้งที่ Mouse Move
       */
      if (
        now -
          lastActivityUpdateRef.current >=
        ACTIVITY_UPDATE_THROTTLE
      ) {
        resetSessionTimer();
      }
    };

    /**
     * ตรวจสอบ Session หมดอายุ
     */
    const checkSessionExpiry = () => {
      const savedEmpCode =
        getStoredEmpCode();

      /**
       * empcode หาย หรือ Logout จาก Tab อื่น
       */
      if (!savedEmpCode) {
        forceLogout();
        return;
      }

      const storedExpiry =
        localStorage.getItem("sessionExpiry");

      if (!storedExpiry) {
        resetSessionTimer();
        return;
      }

      const parsedExpiry =
        Number(storedExpiry);

      if (
        !Number.isFinite(parsedExpiry) ||
        Date.now() >= parsedExpiry
      ) {
        forceLogout();
      }
    };

    /**
     * ตรวจจับการเปลี่ยน Session จาก Tab อื่น
     */
    const handleStorageChange = (
      event: StorageEvent,
    ) => {
      if (
        event.key === "empcode" ||
        event.key === "sessionExpiry" ||
        event.key === "userToken"
      ) {
        checkSessionExpiry();
      }
    };

    /**
     * กลับเข้ามาที่ Browser Tab
     */
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible"
      ) {
        checkSessionExpiry();
      }
    };

    const events: Array<
      keyof WindowEventMap
    > = [
      "mousemove",
      "keydown",
      "touchstart",
      "scroll",
      "wheel",
    ];

    events.forEach((eventName) => {
      window.addEventListener(
        eventName,
        handleActivity,
        {
          passive: true,
        },
      );
    });

    window.addEventListener(
      "storage",
      handleStorageChange,
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    const intervalId =
      window.setInterval(
        checkSessionExpiry,
        SESSION_CHECK_INTERVAL,
      );

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(
          eventName,
          handleActivity,
        );
      });

      window.removeEventListener(
        "storage",
        handleStorageChange,
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );

      window.clearInterval(intervalId);
    };
  }, [
    forceLogout,
    resetSessionTimer,
  ]);

  /**
   * Loading ตอนตรวจสอบ Application Update
   */
  if (isCheckingUpdate) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator
          size="large"
          color="#ffffff"
        />

        <Text style={styles.loadingText}>
          กำลังตรวจสอบอัปเดตล่าสุด...
        </Text>
      </View>
    );
  }

  /**
   * Loading ตอนกู้คืน Session ของ Web
   */
  if (
    isRestoring &&
    Platform.OS === "web"
  ) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator
          size="large"
          color="#ffffff"
        />

        <Text style={styles.loadingText}>
          กำลังกู้คืนข้อมูลผู้ใช้งาน...
        </Text>
      </View>
    );
  }

  /**
   * หน้าจอหลัก
   */
  return (
    <View style={styles.screenWrapper}>
      <View style={styles.appContainer}>
        <Stack />

        {showMenubar && <Menubar />}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    backgroundColor: "#F17736",
  },

  loadingText: {
    marginTop: 15,
    color: "#ffffff",
    fontFamily: "Kanit",
    fontSize: 16,
    textAlign: "center",
  },

  screenWrapper: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
  },

  appContainer: {
    flex: 1,
    height: "100%",
    backgroundColor: "#ffffff",

    ...Platform.select({
      web: {
        width: "100%",
        maxWidth: 1200,
        boxShadow:
          "0 0 20px rgba(0,0,0,0.15)",
      },
    }),
  },
});

export default function RootLayout() {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <MainLayout />
      </LanguageProvider>
    </Provider>
  );
}